import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle, XCircle, Camera, ArrowLeft, WarningCircle } from "@phosphor-icons/react";

export default function Scanner() {
  const { user } = useAuth();
  const scannerRef = useRef(null);
  const busyRef = useRef(false);
  const [scanning, setScanning] = useState(false);
  const [result, setResult] = useState(null); // {valid, message, ...}
  const [manual, setManual] = useState("");
  const [camError, setCamError] = useState("");

  const validate = async (token) => {
    if (busyRef.current) return;
    busyRef.current = true;
    try {
      const { data } = await api.post("/scan/validate", { token });
      setResult(data);
    } catch (e) {
      setResult({ valid: false, message: formatApiErrorDetail(e.response?.data?.detail) || "Gagal validasi" });
    }
    setTimeout(() => { busyRef.current = false; setResult(null); }, 3500);
  };

  const startCamera = async () => {
    setCamError("");
    const html5 = new Html5Qrcode("qr-reader");
    scannerRef.current = html5;
    try {
      await html5.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 240, height: 240 } },
        (decodedText) => validate(decodedText),
        () => {}
      );
      setScanning(true);
    } catch (e) {
      setCamError("Tidak dapat mengakses kamera. Gunakan input manual di bawah atau izinkan akses kamera.");
    }
  };

  const stopCamera = async () => {
    try {
      if (scannerRef.current) { await scannerRef.current.stop(); scannerRef.current.clear(); }
    } catch (e) {}
    scannerRef.current = null;
    setScanning(false);
  };

  useEffect(() => () => { if (scannerRef.current) { scannerRef.current.stop().catch(() => {}); } }, []);

  const overlay = result && (
    <div
      data-testid="scan-result"
      className={`fixed inset-0 z-50 flex flex-col items-center justify-center text-center p-6 ${result.valid ? "bg-[#00FF66]/20" : "bg-[#FF3B30]/20"} backdrop-blur-md`}
    >
      {result.valid ? (
        <CheckCircle weight="fill" size={110} className="text-[#00FF66] mb-6" />
      ) : (
        <XCircle weight="fill" size={110} className="text-[#FF3B30] mb-6" />
      )}
      <h2 className={`font-heading text-4xl font-black mb-3 ${result.valid ? "text-[#00FF66]" : "text-[#FF3B30]"}`}>
        {result.valid ? "TIKET VALID" : result.message?.toUpperCase()}
      </h2>
      {result.attendee_name && (
        <div className="text-white space-y-1">
          <p className="text-2xl font-bold">{result.attendee_name}</p>
          <p className="text-lg text-zinc-300">{result.ticket_type_name} · {result.event_name}</p>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-black text-white">
      {overlay}
      <div className="max-w-lg mx-auto px-5 py-6">
        <div className="flex items-center justify-between mb-6">
          <Link to="/" data-testid="scanner-back" className="flex items-center gap-1.5 text-zinc-400 hover:text-primary text-sm font-semibold">
            <ArrowLeft size={16} weight="bold" /> Kembali
          </Link>
          <span className="text-xs uppercase tracking-widest text-zinc-500">Petugas: {user?.name}</span>
        </div>

        <h1 className="font-heading text-3xl font-black tracking-tight mb-1 flex items-center gap-2">
          <Camera weight="fill" className="text-primary" /> Scan Tiket
        </h1>
        <p className="text-zinc-500 text-sm mb-6">Arahkan kamera ke QR Code pengunjung untuk validasi kehadiran.</p>

        <div className="relative bg-zinc-950 border border-white/10 aspect-square overflow-hidden">
          <div id="qr-reader" className="w-full h-full [&_video]:object-cover [&_video]:w-full [&_video]:h-full" />
          {scanning && (
            <>
              <div className="absolute top-6 left-6 w-10 h-10 border-t-2 border-l-2 border-primary" />
              <div className="absolute top-6 right-6 w-10 h-10 border-t-2 border-r-2 border-primary" />
              <div className="absolute bottom-6 left-6 w-10 h-10 border-b-2 border-l-2 border-primary" />
              <div className="absolute bottom-6 right-6 w-10 h-10 border-b-2 border-r-2 border-primary" />
            </>
          )}
          {!scanning && (
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <Camera size={48} className="text-zinc-700 mb-4" />
              <Button data-testid="start-camera-btn" onClick={startCamera} className="bg-primary text-black font-bold hover:bg-cyan-300 rounded-none">
                Mulai Kamera
              </Button>
            </div>
          )}
        </div>

        {scanning && (
          <Button data-testid="stop-camera-btn" onClick={stopCamera} variant="outline" className="w-full mt-3 border-white/20 rounded-none hover:bg-white hover:text-black">
            Hentikan Kamera
          </Button>
        )}

        {camError && (
          <p className="text-sm text-warning text-yellow-400 mt-3 flex items-center gap-1.5"><WarningCircle size={16} /> {camError}</p>
        )}

        <div className="mt-8 border-t border-white/10 pt-6">
          <p className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Input Manual (Kode Tiket)</p>
          <div className="flex gap-2">
            <Input data-testid="manual-token-input" value={manual} onChange={(e) => setManual(e.target.value)}
              placeholder="Tempel kode tiket di sini" className="bg-zinc-900 border-zinc-800 focus:ring-primary focus:border-primary rounded-none" />
            <Button data-testid="manual-validate-btn" onClick={() => { if (manual.trim()) { validate(manual.trim()); setManual(""); } }}
              className="bg-primary text-black font-bold hover:bg-cyan-300 rounded-none">Validasi</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
